/* @flow */
import type { GetChatStreamItems_chatStreams_sequenceItems } from 'chat/components/ChatStream/queries/__generated__/GetChatStreamItems';
import type { GetAnnouncementStreamItems_entityStreams_sequenceItems } from 'chat/components/AnnouncementStream/queries/__generated__/GetAnnouncementStreamItems';

export type SequenceItem =
  | GetAnnouncementStreamItems_entityStreams_sequenceItems
  | GetChatStreamItems_chatStreams_sequenceItems;

export type UnchunkedSequenceItems =
  | $ReadOnlyArray<GetAnnouncementStreamItems_entityStreams_sequenceItems>
  | $ReadOnlyArray<GetChatStreamItems_chatStreams_sequenceItems>;

export type StreamItemsChunk = {
  type: 'GapItem' | 'PhoneCallItem' | 'MessageItem' | 'SystemMessageItem',
  date?: Date,
  items: Array<SequenceItem>,
  sender?: string,
  key: () => string,
};

export type DateSeparatorChunk = {
  type: 'DateSeparator',
  key: () => string,
  date: Date,
};

export type Chunks = Array<StreamItemsChunk | DateSeparatorChunk>;

function IsSameDate(a: Date, b: ?Date) {
  return (
    !!b &&
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function newStreamItemsChunk(item: SequenceItem): StreamItemsChunk {
  return {
    type: item.item.__typename,
    date:
      (item.item.__typename === 'MessageItem' || item.item.__typename === 'PhoneCallItem') &&
      item.item.createdAt
        ? new Date(item.item.createdAt)
        : undefined,
    sender:
      (item.item.__typename === 'MessageItem' || item.item.__typename === 'PhoneCallItem') &&
      item.item.sender
        ? item.item.sender.uuid
        : undefined,
    items: [item],
    key() {
      return this.items
        .map(i => i.key)
        .sort()
        .join('_');
    },
  };
}

function newDateSeparatorChunk(date: Date): DateSeparatorChunk {
  return {
    type: 'DateSeparator',
    date: new Date(date),
    key: () => date.toUTCString(),
  };
}

const MESSAGE_BUNCH_TIME_WINDOW = 1000 * 60 * 5; // 5 min

/*
 * A chunk is a set of messages with:
 *
 *  - the same type
 *  - the same sender
 *  - sent within 5 mins (MESSAGE_BUNCH_TIME_WINDOW)
 *
 */
function belongToChunk(message: SequenceItem, chunk: StreamItemsChunk): boolean {
  if (message.item.type === 'SystemMessageItem') {
    return false;
  }

  if (message.item.__typename !== chunk.type) {
    return false;
  }

  if (
    (message.item.__typename === 'MessageItem' || message.item.__typename === 'PhoneCallItem') &&
    message.item.createdAt &&
    !IsSameDate(new Date(message.item.createdAt), chunk.date)
  ) {
    return false;
  }

  if (message.item.sender && message.item.sender.uuid !== chunk.sender) {
    return false;
  }

  if (
    (message.item.__typename === 'MessageItem' || message.item.__typename === 'PhoneCallItem') &&
    chunk.date &&
    message.item.createdAt &&
    new Date(message.item.createdAt) - chunk.date > MESSAGE_BUNCH_TIME_WINDOW
  ) {
    return false;
  }

  return true;
}

/* chunkStreamItems take an sorted array of stream message
 * and returns an array of stream items chunks.
 *
 * in: [
 *   { __typename: 'MessageItem', body: 'Hello',          sender: { uuid: 'aaa' } },
 *   { __typename: 'MessageItem', body: 'How are you?',   sender: { uuid: 'aaa' } },
 *   { __typename: 'MessageItem', body: 'Hello',          sender: { uuid: 'bbb' } },
 *   { __typename: 'MessageItem', body: 'Good! and you?', sender: { uuid: 'bbb' } },
 * ]
 *
 *
 * out: [
 *   {
 *     type: 'MessageItem',
 *     items: [
 *       { __typename: 'MessageItem', body: 'Hello',          sender: { uuid: 'aaa' } },
 *       { __typename: 'MessageItem', body: 'How are you?',   sender: { uuid: 'aaa' } },
 *     ],
 *   },
 *   {
 *     type: 'ChatMessage',
 *     items: [
 *       { __typename: 'MessageItem', body: 'Hello',          sender: { uuid: 'bbb' } },
 *       { __typename: 'MessageItem', body: 'Good! and you?', sender: { uuid: 'bbb' } },
 *     ],
 *   },
 * ]
 */
function chunkStreamItems(items: UnchunkedSequenceItems): Array<StreamItemsChunk> {
  if (items.length === 0) {
    return [];
  }

  const stack = Array.from(items);
  const chunks = [newStreamItemsChunk(stack.pop())];

  while (stack.length > 0) {
    const chunk = chunks[chunks.length - 1];
    const message = stack.pop();

    if (belongToChunk(message, chunk)) {
      chunk.items.push(message);
    } else {
      chunks.push(newStreamItemsChunk(message));
    }
  }

  return chunks;
}

/* insertDateBetweenStreamItemsChunks inserts DateSeparatorChunk in between
 * StreamItemsChunk when appropriate (when the day differs).
 *
 * in: [
 *   {
 *     type: 'MessageItem',
 *     items: [ ... ],
 *   },
 *   {
 *     type: 'ChatMessage',
 *     items: [ ... ],
 *   },
 * ]
 *
 * out: [
 *  {
 *    type: 'DateSeparator',
 *    date: 'January 2 2018'
 *  },
 *  {
 *    type: 'MessageItem',
 *    items: [ ... ],
 *  },
 *  {
 *    type: 'DateSeparator',
 *    date: 'January 3 2018'
 *  },
 *  {
 *    type: 'MessageItem',
 *    items: [ ... ],
 *  },
 * ]
 */
function insertDateBetweenStreamItemsChunks(chunks: Array<StreamItemsChunk>): Chunks {
  if (chunks.length === 0) {
    // $FlowFixMe Array<StreamItemsChunk> is subtype of type Chunks.
    return chunks;
  }

  const [headChunk, ...restChunks] = chunks;

  const items = [];
  let lastChunkDate = null;

  // If array head if a GapItem, add it to the result.
  // This is because we never want a date above it.
  if (headChunk.type === 'GapItem') {
    items.push(headChunk);
  } else {
    restChunks.unshift(headChunk);
  }

  for (const chunk of restChunks) {
    const { date } = chunk;

    if (date) {
      const differentDateThanPreviousChunk = lastChunkDate && !IsSameDate(date, lastChunkDate);
      const isFirstChunkInStream = !items.length;

      if (differentDateThanPreviousChunk || isFirstChunkInStream) {
        items.push(newDateSeparatorChunk(date));
      }
    }

    items.push(chunk);

    if (date) {
      lastChunkDate = date;
    }
  }

  return items;
}

export function make(messages: UnchunkedSequenceItems): Chunks {
  return insertDateBetweenStreamItemsChunks(chunkStreamItems(messages));
}
